import { Button, Input, Modal, Select } from '@components/common';
import { UserRole } from '@shared/types';
import React, { useEffect, useMemo, useState } from 'react';
import { CreateUserAccountModalProps } from '../../../types/userManagement';

export const ROLE_OPTIONS = [
  { value: UserRole.SUPER, label: 'Super' },
  { value: UserRole.ADMIN, label: 'Admin' },
  { value: UserRole.MANAGER, label: 'Manager' },
  { value: UserRole.WAITER, label: 'Waiter' },
  { value: UserRole.CASHIER, label: 'Cashier' },
  { value: UserRole.RECEPTIONIST, label: 'Receptionist' },
  { value: UserRole.ACCOUNTANT, label: 'Accountant' },
  { value: UserRole.KDS, label: 'KDS' },
];

const generateTemporaryPassword = () =>
  `SP-${crypto.randomUUID().replace(/-/g, '').slice(0, 10)}`;

export const CreateUserAccountModal: React.FC<CreateUserAccountModalProps> = ({
  isOpen,
  isCreating,
  defaultOutletId,
  outlets,
  onClose,
  onSubmit,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(generateTemporaryPassword);
  const [outletId, setOutletId] = useState(defaultOutletId || '');
  const [role, setRole] = useState<UserRole>(UserRole.WAITER);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword(generateTemporaryPassword());
    setOutletId(defaultOutletId || outlets[0]?.id || '');
    setRole(UserRole.WAITER);
    setError(null);
  }, [defaultOutletId, isOpen, outlets]);

  const outletOptions = useMemo(
    () =>
      outlets.map((outlet) => ({
        value: outlet.id,
        label: outlet.name,
      })),
    [outlets],
  );

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError('First name, last name and email are required');
      return;
    }

    if (!password || password.length < 8) {
      setError('Temporary password must be at least 8 characters long');
      return;
    }

    if (!outletId) {
      setError('Select an outlet for this user');
      return;
    }

    setError(null);
    await onSubmit({
      firstName,
      lastName,
      email,
      password,
      outletId,
      role,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create User Account"
      size="md"
    >
      <div className="space-y-4 text-sm text-gray-900">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            value={firstName}
            onChange={(value) => setFirstName(String(value))}
            placeholder="Enter first name"
          />
          <Input
            label="Last Name"
            value={lastName}
            onChange={(value) => setLastName(String(value))}
            placeholder="Enter last name"
          />
        </div>

        <Input
          label="Email Address"
          type="email"
          value={email}
          onChange={(value) => setEmail(String(value))}
          placeholder="name@company.com"
        />

        <div className="space-y-2 rounded-xl border border-gray-200 p-4 bg-app-bg">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-black">Temporary Password</p>
              <p className="text-xs text-gray-600">
                Share this with the employee so they can sign in on the desktop
                app.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPassword(generateTemporaryPassword())}
              disabled={isCreating}
            >
              Generate
            </Button>
          </div>
          <Input
            value={password}
            onChange={(value) => setPassword(String(value))}
            placeholder="Temporary password"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Outlet"
            value={outletId}
            onChange={setOutletId}
            options={outletOptions}
            placeholder="Select outlet"
          />
          <Select
            label="Role"
            value={role}
            onChange={(value) => setRole(value as UserRole)}
            options={ROLE_OPTIONS}
          />
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={isCreating}>
            Create Account
          </Button>
        </div>
      </div>
    </Modal>
  );
};
